import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { EditorialMarquee } from "@/components/site/Marquee";
import { Reveal } from "@/components/site/Reveal";
import { ArrowRight } from "lucide-react";

const CLINIC_IMG = "https://static.prod-images.emergentagent.com/jobs/985943a1-da20-48a8-8068-987a05536a80/images/cc3ac39984f32d77c3d24c0c0860b78a88fb1050a12c3c59dcb3916eaa643e7c.jpeg";
const WORLD_MAP = "https://static.prod-images.emergentagent.com/jobs/985943a1-da20-48a8-8068-987a05536a80/images/21e0efa4e28cb82861fe5d7620f2d334338c6078bbaf3c7025368409b5bf9ab8.jpeg";
const LOCATIONS = [
    { key: "korea", x: 83.0, y: 36.0 },
    { key: "indonesia", x: 80.0, y: 58.0 },
    { key: "malaysia", x: 77.5, y: 53.5 },
    { key: "myanmar", x: 75.0, y: 45.0 },
    { key: "china", x: 78.5, y: 37.0 },
];

export default function About() {
    const { t } = useLang();
    return (
        <div className="bg-background min-h-screen">
            <Header />

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-36 pb-20">
                <Reveal className="eyebrow text-cyan mb-6">{t.about.eyebrow}</Reveal>
                <Reveal delay={0.05}>
                    <h1 className="font-heading font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter text-obsidian leading-[0.85] max-w-4xl">
                        {t.about.title}
                    </h1>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-10 max-w-2xl text-xl text-muted-foreground leading-relaxed">{t.about.intro}</p>
                </Reveal>
            </section>

            <EditorialMarquee />

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
                <Reveal className="eyebrow text-cyan mb-4">{t.about.howEyebrow}</Reveal>
                <Reveal delay={0.05}>
                    <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight text-obsidian leading-[0.95] max-w-2xl">
                        {t.about.howTitle}
                    </h2>
                </Reveal>
                <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-xl overflow-hidden border border-border">
                    {t.about.steps.map((s, i) => (
                        <Reveal key={i} delay={i * 0.08} className="bg-card p-8 hover:bg-secondary transition-colors duration-500">
                            <div className="font-mono-tech text-cyan text-lg mb-6">{String(i + 1).padStart(2, "0")}</div>
                            <h3 className="font-heading font-bold text-xl text-obsidian mb-3">{s.t}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* GLOBAL PRESENCE */}
            <section className="relative bg-obsidian text-white grain overflow-hidden py-28">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-5">
                        <Reveal className="eyebrow text-cyan mb-4">{t.about.globalEyebrow}</Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[0.95]">
                                {t.about.globalTitle.split("\n").map((l, i) => <span key={i} className="block">{l}</span>)}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-white/60 text-lg leading-relaxed">{t.about.globalDesc}</p>
                        </Reveal>
                        <Reveal delay={0.15} className="mt-8 flex flex-wrap gap-3" data-testid="global-countries">
                            {LOCATIONS.map((l) => (
                                <span key={l.key} className="eyebrow flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-white/80">
                                    <span className="w-2 h-2 rounded-full bg-cyan" /> {t.about.countries[l.key]}
                                </span>
                            ))}
                        </Reveal>
                    </div>
                    <Reveal delay={0.1} className="lg:col-span-7">
                        <div className="relative rounded-2xl overflow-hidden border border-white/10">
                            <img src={WORLD_MAP} alt="Korea Health & Beauty global presence map" className="w-full block" />
                            {LOCATIONS.map((l) => (
                                <div key={l.key} className="group absolute cursor-pointer" style={{ left: `${l.x}%`, top: `${l.y}%`, transform: "translate(-50%, -50%)" }} data-testid={`map-marker-${l.key}`}>
                                    <span className="relative flex items-center justify-center">
                                        <span className="absolute w-5 h-5 rounded-full bg-cyan/40 animate-ping" />
                                        <span className="w-2.5 h-2.5 rounded-full bg-cyan ring-2 ring-white/80 transition-transform duration-300 group-hover:scale-150" />
                                        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap font-mono-tech text-[10px] tracking-wider uppercase text-obsidian bg-cyan px-2 py-1 rounded opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 shadow-lg z-10">
                                            {t.about.countries[l.key]}
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-32 pt-28">
                <Reveal className="bg-obsidian text-white rounded-2xl grain relative overflow-hidden p-12 sm:p-20 text-center">
                    <h2 className="font-heading font-black text-4xl sm:text-6xl tracking-tighter leading-[0.9]">{t.about.ctaTitle}</h2>
                    <Link to="/contact" className="group eyebrow inline-flex items-center gap-2 mt-10 rounded-full bg-cyan text-obsidian px-8 py-4 hover:-translate-y-0.5 transition-transform duration-300">
                        {t.cta.bookDemo} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                </Reveal>
            </section>

            <Footer />
        </div>
    );
}
